import type { Meta, MetaItem } from "@grafana/faro-web-sdk";

/**
 * Detects whether the document is running as an installed PWA.
 *
 * Checks, in order:
 * - `display-mode: standalone` (Chrome/Edge/Android, installed desktop PWA)
 * - `navigator.standalone === true` (iOS Safari "Add to Home Screen")
 * - `document.referrer` starting with `android-app://` (TWA / Trusted Web Activity)
 *
 * SSR-safe: returns `false` when `window` is unavailable.
 */
export function isPwa(): boolean {
  if (typeof window === 'undefined') return false

  try {
    if (typeof window.matchMedia === 'function'
      && window.matchMedia('(display-mode: standalone)').matches) {
      return true
    }
  } catch {
    // matchMedia not supported or query rejected — fall through
  }

  if ((window.navigator as { standalone?: boolean }).standalone === true) {
    return true
  }

  if (typeof document !== 'undefined'
    && typeof document.referrer === 'string'
    && document.referrer.startsWith('android-app://')) {
    return true
  }

  return false
}

export function isBot(userAgent: string): boolean {
  const bots =
    '(googlebot/|bot|Googlebot-Mobile|Googlebot-Image|Google favicon|Mediapartners-Google|bingbot|slurp|java|wget|curl|Commons-HttpClient|Python-urllib|libwww|httpunit|nutch|phpcrawl|msnbot|jyxobot|FAST-WebCrawler|FAST Enterprise Crawler|biglotron|teoma|convera|seekbot|gigablast|exabot|ngbot|ia_archiver|GingerCrawler|webmon |httrack|webcrawler|grub.org|UsineNouvelleCrawler|antibot|netresearchserver|speedy|fluffy|bibnum.bnf|findlink|msrbot|panscient|yacybot|AISearchBot|IOI|ips-agent|tagoobot|MJ12bot|dotbot|woriobot|yanga|buzzbot|mlbot|yandexbot|purebot|Linguee Bot|Voyager|CyberPatrol|voilabot|baiduspider|citeseerxbot|spbot|twengabot|postrank|turnitinbot|scribdbot|page2rss|sitebot|linkdex|Adidxbot|blekkobot|ezooms|dotbot|Mail.RU_Bot|discobot|heritrix|findthatfile|europarchive.org|NerdByNature.Bot|sistrix crawler|ahrefsbot|Aboundex|domaincrawler|wbsearchbot|summify|ccbot|edisterbot|seznambot|ec2linkfinder|gslfbot|aihitbot|intelium_bot|facebookexternalhit|yeti|RetrevoPageAnalyzer|lb-spider|sogou|lssbot|careerbot|wotbox|wocbot|ichiro|DuckDuckBot|lssrocketcrawler|drupact|webcompanycrawler|acoonbot|openindexspider|gnam gnam spider|web-archive-net.com.bot|backlinkcrawler|coccoc|integromedb|content crawler spider|toplistbot|seokicks-robot|it2media-domain-crawler|ip-web-crawler.com|siteexplorer.info|elisabot|proximic|changedetection|blexbot|arabot|WeSEE:Search|niki-bot|CrystalSemanticsBot|rogerbot|360Spider|psbot|InterfaxScanBot|Lipperhey SEO Service|CC Metadata Scaper|g00g1e.net|GrapeshotCrawler|urlappendbot|brainobot|fr-crawler|binlar|SimpleCrawler|Livelapbot|Twitterbot|cXensebot|smtbot|bnf.fr_bot|A6-Indexer|ADmantX|Facebot|Twitterbot|OrangeBot|memorybot|AdvBot|MegaIndex|SemanticScholarBot|ltx71|nerdybot|xovibot|BUbiNG|Qwantify|archive.org_bot|Applebot|TweetmemeBot|crawler4j|findxbot|SemrushBot|yoozBot|lipperhey|y!j-asr|Domain Re-Animator Bot|AddThis|'
    // Generic crawler/automation families + specific Google/Bing preview agents
    // and headless Chrome — broadens coverage beyond the named-bot list above.
    + 'crawler|spider|scraper|fetcher|BingPreview|GoogleOther|Google-NotebookLM|Google-Read-Aloud|Google Web Preview|HeadlessChrome|Headless)'

  const botsRegex = new RegExp(bots, 'i')

  return botsRegex.test(userAgent)
}

/**
 * Automation / headless signal that isn't in the UA string: `navigator.webdriver`
 * is `true` under Selenium / Puppeteer / Playwright and headless drivers.
 * Complements {@link isBot} (which catches `HeadlessChrome` UAs) for automated
 * clients that spoof a normal UA. SSR-safe.
 */
export function isAutomated(): boolean {
  return typeof navigator !== 'undefined' && (navigator as { webdriver?: boolean }).webdriver === true
}

/**
 * UTM parameter keys recognized by the SDK. Keys are written and read as
 * snake_case (industry standard — matches GA4, GTM, and Faro session attributes).
 * `utm_id` was added in GA4 for unique campaign identification.
 */
export const UTM_PARAM_KEYS = [
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_content',
  'utm_term',
  'utm_id',
] as const

/**
 * Ad click-id params captured alongside UTMs — the provider stamps these on the
 * landing URL and they're key for paid-acquisition attribution.
 *
 * O click id é a testemunha mais forte da atribuição, porque quem carimba é a
 * PLATAFORMA e não quem montou o link: ele chega em cliques que muitas vezes não
 * têm `utm_*` nenhum, e sem ele essa sessão é contada como tráfego DIRETO — a
 * maior subcontagem de mídia paga que existe. E é justamente nas plataformas de
 * social que o `utm_*` mais se perde no caminho (navegador in-app, encurtador,
 * redirect do app), onde o carimbo sobrevive.
 *
 * `gbraid`/`wbraid` são os substitutos do `gclid` quando o consentimento do iOS
 * limita o cookie do Google — sem eles, a mesma campanha aparece atribuída no
 * Android e direta no iPhone.
 *
 * O que cada um PROVA é decisão de quem lê, não daqui: este módulo só coleta. A
 * nuance que mais engana está no `fbclid`, que a Meta carimba em qualquer link
 * de saída (inclusive num post orgânico compartilhado) e portanto prova a
 * plataforma, não que houve verba.
 */
export const CLICK_ID_KEYS = [
  // Google Ads
  'gclid',
  'gbraid',
  'wbraid',
  // Microsoft Advertising
  'msclkid',
  // Meta
  'fbclid',
  // TikTok
  'ttclid',
  // LinkedIn
  'li_fat_id',
  // X
  'twclid',
  // Pinterest
  'epik',
] as const

export type UtmParamKey = typeof UTM_PARAM_KEYS[number] | typeof CLICK_ID_KEYS[number]

/**
 * Read UTM + ad click-id params (utm_*, gclid) from the URL. Empty values are
 * omitted. The result is spread into the Faro session so every event carries
 * the acquisition context.
 */
export function getUtmParams(searchParams: URLSearchParams): Partial<Record<UtmParamKey, string>> {
  const result: Partial<Record<UtmParamKey, string>> = {}
  for (const key of [...UTM_PARAM_KEYS, ...CLICK_ID_KEYS]) {
    const value = searchParams.get(key)
    if (value) result[key] = value
  }
  return result
}

export function getPageMeta(url: URL) {
  const domain = url.hostname
  const path = url.pathname
  const queryString = url.search
  const href = url.href

  const pageMeta: MetaItem<Pick<Meta, 'page'>> = {
    page: {
      url: href,
      attributes: {
        domain,
        path,
        queryString,
        ...getUtmParams(url.searchParams),
      },
    },
  }

  return pageMeta
}


function getNodeName(node: Node) {
  const name = node.nodeName;
  return node.nodeType === 1
    ? name.toLowerCase()
    : name.toUpperCase().replace(/^#/, '');
}

const MAX_LEN = 100;

export function getNodeSelector(node: Node | null) {
  let sel = '';

  try {
    while (node?.nodeType !== 9) {
      const el: Element = node as Element;
      const part = el.id
        ? '#' + el.id
        : [getNodeName(el), ...Array.from(el.classList).sort()].join('.');
      if (sel.length + part.length > MAX_LEN - 1) {
        return sel || part;
      }
      sel = sel ? part + '>' + sel : part;
      if (el.id) {
        break;
      }
      node = el.parentNode;
    }
  } catch {
    // Do nothing...
  }
  return sel;
};
